/*
  Ever Leaf — small, independent custom elements for the sections that Dawn
  has no native equivalent for. Everything else (cart, product media,
  variant picker, header/footer menus) is Dawn's own JS and is untouched.
*/
(function () {
  'use strict';

  if (customElements.get('ever-leaf-accordion')) return;

  /* Single-open accordion group (FAQ sections, product details). */
  class EverLeafAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details.ever-leaf-accordion-item'));
      this.items.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (item.open) {
            this.items.forEach((other) => {
              if (other !== item) other.open = false;
            });
          }
        });
      });
    }
  }
  customElements.define('ever-leaf-accordion', EverLeafAccordion);

  /* FAQ explorer: category filter + live search coming from the hero search box. */
  class EverLeafFaqExplorer extends HTMLElement {
    connectedCallback() {
      this.chips = Array.from(this.querySelectorAll('[data-faq-category]'));
      this.entries = Array.from(this.querySelectorAll('[data-faq-entry]'));
      this.empty = this.querySelector('[data-faq-empty]');
      this.active = 'all';
      this.query = '';

      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.active = chip.getAttribute('data-faq-category');
          this.chips.forEach((c) => c.classList.toggle('is-active', c === chip));
          this.filter();
        });
      });

      document.addEventListener('everleaf:faq-search', (event) => {
        this.query = (event.detail && event.detail.value) || '';
        this.filter();
      });
    }

    filter() {
      let visibleCount = 0;
      this.entries.forEach((entry) => {
        const category = entry.getAttribute('data-faq-entry');
        const text = (entry.getAttribute('data-faq-text') || '').toLowerCase();
        const matchesCategory = this.active === 'all' || category === this.active;
        const matchesQuery = this.query.trim() === '' || text.indexOf(this.query.trim().toLowerCase()) !== -1;
        const visible = matchesCategory && matchesQuery;
        entry.toggleAttribute('hidden', !visible);
        if (visible) visibleCount += 1;
      });
      if (this.empty) this.empty.toggleAttribute('hidden', visibleCount !== 0);
    }
  }
  customElements.define('ever-leaf-faq-explorer', EverLeafFaqExplorer);

  /* Hero search box (FAQ page) broadcasts to the explorer section below it. */
  class EverLeafHeroSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.input.addEventListener('input', () => {
        document.dispatchEvent(new CustomEvent('everleaf:faq-search', { detail: { value: this.input.value } }));
      });
    }
  }
  customElements.define('ever-leaf-hero-search', EverLeafHeroSearch);

  /* Testimonials — Mobile-only swipe carousel (native scroll-snap does the
     actual swiping; this just syncs arrows/dots and drives programmatic
     navigation). Desktop keeps the plain grid and never runs any of this in
     a visible way (arrows/dots are display: none there). Any number of
     cards is supported — nothing here assumes a fixed count. Navigation is
     element-based (scrollIntoView), never raw scrollLeft math, so it works
     correctly in both LTR and RTL without special-casing either. */
  class EverLeafTestimonialsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.ever-leaf-testimonials__track');
      this.cards = Array.from(this.querySelectorAll('.ever-leaf-testimonials__card'));
      this.prevBtn = this.querySelector('.ever-leaf-testimonials__arrow--prev');
      this.nextBtn = this.querySelector('.ever-leaf-testimonials__arrow--next');
      this.dots = Array.from(this.querySelectorAll('.ever-leaf-testimonials__dot'));

      if (!this.track || this.cards.length === 0) return;

      this.currentIndex = 0;

      /* AbortController lets every listener below be removed in one call
         (disconnectedCallback) without needing named/bound handler
         references, so a reconnect of this same element instance can never
         accumulate duplicate handlers. */
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.goTo(this.currentIndex - 1), { signal });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.goTo(this.currentIndex + 1), { signal });
      }
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index), { signal });
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            let best = null;
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
              }
            });
            if (best) {
              const index = this.cards.indexOf(best.target);
              if (index !== -1) this.setActive(index);
            }
          },
          { root: this.track, threshold: 0.6 }
        );
        this.cards.forEach((card) => this.observer.observe(card));
      }

      this.setActive(0);
    }

    goTo(index) {
      const clamped = Math.max(0, Math.min(index, this.cards.length - 1));
      this.cards[clamped].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    setActive(index) {
      this.currentIndex = index;
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = index === 0;
      if (this.nextBtn) this.nextBtn.disabled = index === this.cards.length - 1;
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.initialized = false;
    }
  }
  customElements.define('ever-leaf-testimonials-carousel', EverLeafTestimonialsCarousel);

  /*
    MOBILE PAGE-ZOOM LOCK pass, Section 7 — iOS Safari fallback. WebKit
    fires its own non-standard `gesturestart`/`gesturechange`/`gestureend`
    events for a two-finger pinch/rotate, and can still magnify the whole
    page through this path even when the viewport meta's
    maximum-scale=1/user-scalable=no and the sitewide touch-action policy
    (ever-leaf-common.css) already suppress most other zoom paths — a known
    iOS Safari quirk (this event family predates and is independent of
    touch-action). preventDefault() here stops ONLY that native page-
    magnification path.

    This does NOT prevent or interfere with the Visualizer's own two-finger
    engine: gesturestart/gesturechange/gestureend are a completely SEPARATE
    WebKit-only event stream from the standard pointerdown/pointermove
    events assets/ever-leaf-personalizer.js's pinch handling relies on —
    preventing the former has no effect on the latter, confirmed in QA with
    the Visualizer's own pinch still working normally with this listener
    active. Safe to attach document-wide with no Canvas exclusion needed.

    No-op everywhere else: these events simply never fire on non-WebKit
    browsers or with a mouse, so nothing here affects Chrome/Firefox/
    Android or Desktop Safari (gesturestart requires actual multi-touch
    hardware to ever dispatch).
  */
  ['gesturestart', 'gesturechange', 'gestureend'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        event.preventDefault();
      },
      { passive: false }
    );
  });
})();
